/**
 * Sends the app's analytics events to PostHog after the visitor permits them.
 * Translates app values into PostHog data and enforces the separate statistics
 * and recording choices before collecting or sending.
 */
import posthog from "posthog-js"
import { analytics, AnalyticsEventName, type AnalyticsEvent } from "@/utils/analytics"
import { SearchType } from "@/types/searchType"
import { translateToEnglish } from "@/lang/i18n"
import { formatDateToISO } from "@/utils/date"
import type { MapStyleValue } from "@/components/constants"
import { EvaluationStatus, type LayerTypeValue, OpportunityType, TransactionMetadataStatus } from "@/model/DataModel"
import type { Filters } from "@/model/RootStore"
import type { AuthenticationProvider } from "@/utils/authenticationProvider"
import { isPrivateRoute } from "@/utils/trackingLocation"
import {
	evaluationStatusTranslationKeys,
	transactionMetadataStatusTranslationKeys,
} from "@/utils/filterTranslationKeys"

type AnalyticsChange = {
	from: string
	to: string
}

type ValueChange<Value> = {
	from: Value
	to: Value
}

type SelectFilterValue = (string | number | null)[] | string | number | null

type RangeFilterChange = ValueChange<[number, number]>

type DateRange = {
	start: Date | null
	end: Date | null
}

type PostHogUser = {
	id: string
	email?: string
	name: string
	role: string
}

type PostHogEventProperties = {
	sign_in_initiated: { provider: AuthenticationProvider }
	user_signed_in: { provider: AuthenticationProvider }
	user_signed_out: undefined
	buyers_list_exported: { userId: string | null }
	navigation_clicked: { navigation: string }
	opportunity_selected: { id: string | number; type: string }
	search_interacted: { type: string; value: string }
	layer_changed: { layer: string; active: boolean }
	base_map_changed: { baseMap: string }
	filter_changed: { filter: string; from: string; to: string }
	save_search_saved: { name: string; userId: string | null }
	opportunity_interacted: {
		opportunityId: number
		action: string
		comment?: string
		from?: string
		to?: string
	}
}

type PostHogEventName = keyof PostHogEventProperties

const baseMapNames: Record<MapStyleValue, string> = {
	"map-style-simple": "vector-light",
	"map-style-standard": "vector-street",
	"map-style-satellite": "satellite-mapbox",
	"map-style-google-maps": "satellite-google",
	"map-style-mern-qc": "satellite-mern-qc",
}

const opportunityTypeTranslationKeys: Record<OpportunityType, string> = {
	[OpportunityType.ListingLanderz]: "opportunities-layers.listing_landerz",
	[OpportunityType.ListingPotential]: "opportunities-layers.listing_potential",
	[OpportunityType.OffMarketOfficial]: "opportunities-layers.off_market_official",
	[OpportunityType.OpportunityTarget]: "opportunities-layers.opportunity_target",
}

const searchTypeTranslationKeys: Record<SearchType, string> = {
	[SearchType.ADDRESS]: "search.byAddress",
	[SearchType.PARCEL]: "search.byParcel",
	[SearchType.OPPORTUNITY]: "search.byOpportunity",
	[SearchType.TRANSACTION]: "search.byTransaction",
}

const opportunityInteractionTranslationKeys = {
	copyLink: "callToActions.copyLink",
	like: "opportunities.interactions.like",
	dislike: "opportunities.interactions.dislike",
	createTarget: "callToActions.createOpportunity",
	googleMaps: "callToActions.openInMaps",
	listingPage: "callToActions.openDetailedOpportunity",
	hubspot: "callToActions.openDeal",
	contract: "callToActions.openContract",
	contact: "opportunities.buttonCTAOffmarket",
	addComment: "opportunities.comments.add_comment_button",
	deleteTarget: "opportunities.deleteButton",
	pipelineStage: "opportunities.stage",
	transfer: "opportunities.transferButton",
	makeCcv: "opportunities.makeCcvButton",
	priority: "opportunities.priority",
} as const

type OpportunityInteractionAction = keyof typeof opportunityInteractionTranslationKeys
type OpportunityInteractionChangeAction = "pipelineStage" | "priority"
type OpportunityInteractionSimpleAction = Exclude<
	OpportunityInteractionAction,
	"addComment" | OpportunityInteractionChangeAction | "transfer"
>

type OpportunityInteractionBase = {
	opportunityId: number
}

type OpportunityInteraction =
	| (OpportunityInteractionBase & { action: OpportunityInteractionSimpleAction })
	| (OpportunityInteractionBase & { action: "addComment"; comment: string })
	| (OpportunityInteractionBase & { action: OpportunityInteractionChangeAction; from: string; to: string })
	| (OpportunityInteractionBase & { action: "transfer"; to: string })

const booleanFilterParentLayers = {
	endangeredSpecies: "endangered-species",
	transitLinesStatus: "transit-lines",
	transitStationsStatus: "transit-stations",
	planningPolicy: "planning-policy",
} as const

type BooleanLayerFilterGroup = keyof typeof booleanFilterParentLayers
type BooleanLayerFilterKey<Group extends BooleanLayerFilterGroup = BooleanLayerFilterGroup> =
	Group extends BooleanLayerFilterGroup ? keyof Filters[Group] & string : never
type DevPotentialKey = keyof Filters["developmentPotential"] & string
type UrbanPriorityKey = keyof Filters["urbanPriorities"] & string
type UrbanPriorityStatus = keyof Filters["urbanPriorities"][UrbanPriorityKey] & string

const booleanFilterTranslationKeys: Record<BooleanLayerFilterKey, string> = {
	threatenedOrVulnerable: "endangered-species.level.threatened-or-vulnerable",
	susceptible: "endangered-species.level.susceptible",
	candidate: "endangered-species.level.candidate",
	masked: "endangered-species.level.masked",
	inUse: "layers.status.in-use",
	inConstruction: "layers.status.in-construction",
	proposed: "layers.status.proposed",
	tod: "planning-policy.level.tod",
	ppu: "planning-policy.level.ppu",
	corridor: "planning-policy.level.corridor",
	strategicSector: "planning-policy.level.strategic-sector",
}

const filterValueTranslationKeys: Record<string, (value: string) => string | undefined> = {
	"filters.project-status": value => evaluationStatusTranslationKeys[value as EvaluationStatus],
	"filters.priority": value => (value === "all" ? "filters.priority-all" : `opportunities-table.priority.${value}`),
	"filters.asset-type": value => `property.assetType.${value}`,
	"filters.built-category": value => `property.builtCategory.${value}`,
	"filters.built-subcategory": value => `property.builtSubcategory.${value}`,
	"filters.land-category": value => `property.landCategory.${value}`,
	"filters.land-subcategory": value => `property.landSubcategory.${value}`,
	"filters.transaction-status-title": value =>
		transactionMetadataStatusTranslationKeys[value as TransactionMetadataStatus],
	"filters.transaction-verification-status-title": value => `filters.transaction-verification-status-${value}`,
	"filters.buyer-status-title": value => `filters.buyer-status-${value}`,
	"filters.transaction-source-title": value => `filters.transaction-source-${value}`,
	"filters.transaction-composition-title": value => `filters.transaction-composition-${value}`,
}

const nestedLayerParents: Record<string, string> = {
	"land-use": "urban-planning",
}

let initialized = false
let usageAllowed = false
let recordingAllowed = false
const requestController = new AbortController()

function runPostHogOperation(operation: () => void): boolean {
	try {
		operation()

		return true
	} catch (error) {
		console.error("PostHog operation failed:", error)

		return false
	}
}

function capture<EventName extends PostHogEventName>(
	eventName: EventName,
	...args: PostHogEventProperties[EventName] extends undefined ? [] : [PostHogEventProperties[EventName]]
): void {
	if (!initialized || !usageAllowed || requestController.signal.aborted || isPrivateRoute()) {
		return
	}

	const properties = args[0] as Record<string, unknown> | undefined

	if (!properties) {
		runPostHogOperation(() => {
			posthog.capture(eventName)
		})

		return
	}

	runPostHogOperation(() => {
		posthog.capture(eventName, properties)
	})
}

function initialize(): boolean {
	if (!usageAllowed || isPrivateRoute()) return false
	if (initialized) {
		return true
	}

	const enabled = import.meta.env.VITE_ENABLE_POSTHOG === "true"

	if (!enabled) {
		return false
	}

	const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined
	const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com"

	if (!token) {
		if (import.meta.env.DEV) {
			console.error(
				"VITE_POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_PROJECT_TOKEN is configured"
			)
		}

		return false
	}

	const didInitialize = runPostHogOperation(() => {
		// The SDK forwards fetch_options to fetch. A shared signal cancels queued
		// retries as well as active requests; withdrawal must never flush a beacon.
		const fetchOptions: RequestInit & { cache: RequestCache } = {
			cache: "no-store",
			signal: requestController.signal,
			keepalive: false,
		}
		posthog.init(token, {
			api_host: host,
			defaults: "2026-06-25",
			// Do not restore another account's browser identity or recording session.
			// Signed-in activity is still joined through our explicit identify call.
			persistence: "memory",
			autocapture: false,
			capture_pageleave: false,
			request_batching: false,
			api_transport: "fetch",
			disable_beacon: true,
			fetch_options: fetchOptions,
			disable_session_recording: !recordingAllowed,
			before_send: event => {
				if (
					!event ||
					!usageAllowed ||
					requestController.signal.aborted ||
					isPrivateRoute() ||
					(event.event === "$snapshot" && !recordingAllowed)
				)
					return null
				return event
			},
		})
	})

	if (!didInitialize) {
		return false
	}

	initialized = true
	runPostHogOperation(() => posthog.opt_in_capturing({ captureEventName: false }))
	// Subscribe once, after permission. Earlier events are not kept for later.
	analytics.subscribe(handleAnalyticsEvent)

	return true
}

function identify(user: PostHogUser): void {
	if (!initialized || !usageAllowed || requestController.signal.aborted || isPrivateRoute()) {
		return
	}

	runPostHogOperation(() => {
		posthog.identify(user.id, {
			email: user.email,
			name: user.name,
			role: user.role,
		})
	})
}

function captureException(error: unknown): void {
	if (!initialized || !usageAllowed || requestController.signal.aborted || isPrivateRoute()) {
		return
	}

	runPostHogOperation(() => {
		posthog.captureException(error)
	})
}

function resetUser(): void {
	if (!initialized) {
		return
	}

	runPostHogOperation(() => {
		posthog.reset()
	})
}

function userSignedOut(): void {
	capture("user_signed_out")
}

function applyPrivacyChoices(allowUsage: boolean, allowRecording: boolean): boolean {
	if (initialized && ((usageAllowed && !allowUsage) || (recordingAllowed && !allowRecording))) {
		requestController.abort()
	}
	usageAllowed = allowUsage
	recordingAllowed = allowUsage && allowRecording
	if (!usageAllowed) {
		if (initialized)
			runPostHogOperation(() => {
				posthog.stopSessionRecording()
				posthog.opt_out_capturing()
				posthog.reset()
			})
		return false
	}
	if (!initialized) return initialize()
	// A withdrawn runtime is disposed by the page refresh, never reactivated with
	// an old SDK queue or identity. A fresh page creates a fresh request signal.
	if (requestController.signal.aborted) return false
	runPostHogOperation(() => {
		posthog.opt_in_capturing({ captureEventName: false })
		posthog.set_config({ disable_session_recording: !recordingAllowed })
		if (!recordingAllowed) posthog.stopSessionRecording()
	})
	return true
}

function captureFilterChange(filterTranslationKeys: string | string[], change: AnalyticsChange): void {
	if (change.from === change.to) {
		return
	}

	const translationKeys = Array.isArray(filterTranslationKeys) ? filterTranslationKeys : [filterTranslationKeys]

	capture("filter_changed", {
		filter: translationKeys.map(translateToEnglish).join(" - "),
		from: change.from,
		to: change.to,
	})
}

function formatFilterValue(filterTranslationKey: string, value: SelectFilterValue): string {
	const values = Array.isArray(value) ? value : value == null ? [] : [value]
	const translationKey = filterValueTranslationKeys[filterTranslationKey]

	return values
		.map(selectedValue => {
			const rawValue = String(selectedValue)
			const valueTranslationKey = translationKey?.(rawValue)

			return valueTranslationKey ? translateToEnglish(valueTranslationKey) : rawValue
		})
		.join(", ")
}

function filterChanged(filterTranslationKeys: string | string[], change: ValueChange<SelectFilterValue>): void {
	const translationKeys = Array.isArray(filterTranslationKeys) ? filterTranslationKeys : [filterTranslationKeys]
	const valueTranslationKey = translationKeys[translationKeys.length - 1]

	captureFilterChange(translationKeys, {
		from: formatFilterValue(valueTranslationKey, change.from),
		to: formatFilterValue(valueTranslationKey, change.to),
	})
}

function formatRange(range: [number, number]): string {
	return `${range[0]} - ${range[1]}`
}

function rangeFilterChanged(filterTranslationKey: string, change: RangeFilterChange): void {
	captureFilterChange(["filters.others-title", filterTranslationKey], {
		from: formatRange(change.from),
		to: formatRange(change.to),
	})
}

function formatDateRange(range: DateRange): string {
	if (!range.start || !range.end) {
		return ""
	}

	return `${formatDateToISO(range.start)} - ${formatDateToISO(range.end)}`
}

function dateFilterChanged(from: DateRange, to: DateRange): void {
	const formattedFrom = formatDateRange(from)
	const formattedTo = formatDateRange(to)

	captureFilterChange(["filters.others-title", "filters.data-range"], {
		from: formattedFrom,
		to: formattedTo,
	})
}

function navigationClicked(translationKey: string): void {
	capture("navigation_clicked", {
		navigation: translateToEnglish(translationKey),
	})
}

function opportunitySelected(id: string | number, opportunityType: OpportunityType): void {
	capture("opportunity_selected", {
		id,
		type: translateToEnglish(opportunityTypeTranslationKeys[opportunityType]),
	})
}

function searchInteracted(searchType: SearchType, value: string): void {
	const translationKey = searchTypeTranslationKeys[searchType]

	if (!value.trim()) {
		return
	}

	capture("search_interacted", {
		type: translateToEnglish(translationKey),
		value,
	})
}

function opportunityInteracted(properties: OpportunityInteraction): void {
	capture("opportunity_interacted", {
		...properties,
		action: translateToEnglish(opportunityInteractionTranslationKeys[properties.action]),
	})
}

function captureTranslatedLayerChange(translationKeys: string[], active: boolean): void {
	capture("layer_changed", {
		layer: translationKeys.map(translateToEnglish).join(" - "),
		active,
	})
}

function baseMapChanged(style: MapStyleValue): void {
	capture("base_map_changed", { baseMap: baseMapNames[style] })
}

function layerChanged(layerTypeValue: LayerTypeValue, active: boolean): void {
	captureTranslatedLayerChange([`opportunities-layers.${layerTypeValue}`], active)
}

function layerSelectionChanged(layer: string, active: boolean): void {
	const parent = nestedLayerParents[layer]
	const layers = parent ? [parent, layer] : [layer]

	captureTranslatedLayerChange(
		layers.map(layerName => `layers.${layerName}`),
		active
	)
}

function booleanLayerFilterChanged<Group extends BooleanLayerFilterGroup>(
	group: Group,
	filter: BooleanLayerFilterKey<Group>,
	active: boolean
): void {
	captureTranslatedLayerChange(
		[`layers.${booleanFilterParentLayers[group]}`, booleanFilterTranslationKeys[filter]],
		active
	)
}

function urbanPriorityChanged(priority: UrbanPriorityKey, active: boolean): void {
	captureTranslatedLayerChange(
		["layers.urban-priorities", `urban-priorities.level.${priority.replace("_", "-")}`],
		active
	)
}

function urbanPriorityStatusChanged(priority: UrbanPriorityKey, status: UrbanPriorityStatus, active: boolean): void {
	captureTranslatedLayerChange(
		[
			"layers.urban-priorities",
			`urban-priorities.level.${priority.replace("_", "-")}`,
			`urban-priorities.version.${status}`,
		],
		active
	)
}

function developmentPotentialChanged(potential: DevPotentialKey, active: boolean): void {
	captureTranslatedLayerChange(["layers.prospects", `development-potential.${potential}`], active)
}

function handleAnalyticsEvent(event: AnalyticsEvent): void {
	switch (event.name) {
		case AnalyticsEventName.IdentifyUser:
			identify(event.payload)
			break
		case AnalyticsEventName.CaptureException:
			captureException(event.payload)
			break
		case AnalyticsEventName.ResetUser:
			resetUser()
			break
		case AnalyticsEventName.SignInInitiated:
			capture("sign_in_initiated", event.payload)
			break
		case AnalyticsEventName.UserSignedIn:
			capture("user_signed_in", event.payload)
			break
		case AnalyticsEventName.UserSignedOut:
			userSignedOut()
			break
		case AnalyticsEventName.BuyersListExported:
			capture("buyers_list_exported", event.payload)
			break
		case AnalyticsEventName.NavigationClicked:
			navigationClicked(event.payload.translationKey)
			break
		case AnalyticsEventName.OpportunitySelected:
			opportunitySelected(event.payload.id, event.payload.opportunityType)
			break
		case AnalyticsEventName.SearchInteracted:
			searchInteracted(event.payload.searchType, event.payload.value)
			break
		case AnalyticsEventName.LayerChanged:
			layerChanged(event.payload.layerTypeValue, event.payload.active)
			break
		case AnalyticsEventName.LayerSelectionChanged:
			layerSelectionChanged(event.payload.layer, event.payload.active)
			break
		case AnalyticsEventName.BooleanLayerFilterChanged:
			booleanLayerFilterChanged(event.payload.group, event.payload.filter, event.payload.active)
			break
		case AnalyticsEventName.UrbanPriorityChanged:
			urbanPriorityChanged(event.payload.priority, event.payload.active)
			break
		case AnalyticsEventName.UrbanPriorityStatusChanged:
			urbanPriorityStatusChanged(event.payload.priority, event.payload.status, event.payload.active)
			break
		case AnalyticsEventName.DevelopmentPotentialChanged:
			developmentPotentialChanged(event.payload.potential, event.payload.active)
			break
		case AnalyticsEventName.BaseMapChanged:
			baseMapChanged(event.payload.style)
			break
		case AnalyticsEventName.FilterChanged:
			filterChanged(event.payload.filterTranslationKeys, event.payload.change)
			break
		case AnalyticsEventName.RangeFilterChanged:
			rangeFilterChanged(event.payload.filterTranslationKey, event.payload.change)
			break
		case AnalyticsEventName.DateFilterChanged:
			dateFilterChanged(event.payload.from, event.payload.to)
			break
		case AnalyticsEventName.SavedSearchCreated:
			capture("save_search_saved", event.payload)
			break
		case AnalyticsEventName.OpportunityInteracted:
			opportunityInteracted(event.payload)
	}
}

export const posthogAnalyticsAdapter = {
	initialize,
	applyPrivacyChoices,
}
